// BYO-01 "My subscriptions": the queries of the personal page and of Setup › Keys & connections.
// Keys sit under `["company", co, …]` so the change pings of the company reach them.
import type { OperationOutput } from "@companyos/sdk";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type Policy = OperationOutput<"subscriptionPolicy.get">;
export type Credential = OperationOutput<"modelCredentials.list">["data"][number];
export type Connection = OperationOutput<"connections.list">["data"][number];
export type Provider = OperationOutput<"connections.providers">["data"][number];
export type SidebarChannel = OperationOutput<"channels.list">["data"][number];

export const subscriptionKeys = {
  all: (co: string) => ["company", co, "subscriptions"] as const,
  policy: (co: string) => ["company", co, "subscriptions", "policy"] as const,
  credentials: (co: string) => ["company", co, "subscriptions", "credentials"] as const,
  connections: (co: string) => ["company", co, "subscriptions", "connections"] as const,
  providers: (co: string) => ["company", co, "subscriptions", "providers"] as const,
  channels: (co: string, joined: boolean) =>
    ["company", co, "subscriptions", "channels", joined] as const,
};

export const policyQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: subscriptionKeys.policy(co),
    queryFn: () => api.client.subscriptionPolicy.get({ params: { co } }),
  });

export const credentialsQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: subscriptionKeys.credentials(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.modelCredentials.list({
        params: { co },
        query: { limit: 100, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const connectionsQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: subscriptionKeys.connections(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.connections.list({
        params: { co },
        query: { limit: 100, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const providersQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: subscriptionKeys.providers(co),
    queryFn: () => api.client.connections.providers({ params: { co } }),
    staleTime: 5 * 60_000,
  });

export const channelsQuery = (api: Api, co: string, joined: boolean) =>
  infiniteQueryOptions({
    queryKey: subscriptionKeys.channels(co, joined),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.channels.list({
        params: { co },
        query: {
          joined: joined ? "true" : "false",
          limit: 100,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
